import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
  Divider,
  Button,
} from "@mui/material";
import { X, ExternalLink, Rocket } from "lucide-react";
import {
  fetchMarketingOrganization,
  fetchOrgTasks,
  fetchOrgActivity,
} from "../../../../../../../redux/actions/marketingAction";
import { backendClient } from "../../../../../../../api/backend";
import FicheHeader from "./FicheHeader";
import FicheInfo from "./FicheInfo";
import FicheContacts from "./FicheContacts";
import FicheNotes from "./FicheNotes";
import FicheEmail from "./FicheEmail";
import FicheQuotes from "./FicheQuotes";
import FicheTasks from "./FicheTasks";
import FicheActivity from "./FicheActivity";
import OnboardOrgDialog from "../OnboardOrgDialog";

const DRAWER_WIDTH = 680;

const OrganizationFiche = ({ open, onClose, orgId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentOrganization, orgTasks, orgActivity } = useSelector(
    (state) => state.marketing
  );
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      await Promise.all([
        dispatch(fetchMarketingOrganization(orgId)),
        dispatch(fetchOrgTasks(orgId)),
        dispatch(fetchOrgActivity(orgId)),
      ]);
      const contactsData = await backendClient.getMarketingContacts?.({ page_size: 100 });
      setAllContacts(contactsData?.items || []);
    } catch (e) {
      console.error("Failed to load org fiche:", e);
    } finally {
      setLoading(false);
    }
  }, [orgId, dispatch]);

  useEffect(() => {
    if (open && orgId) {
      loadData();
    }
  }, [open, orgId, loadData]);

  const org = currentOrganization?.id === orgId ? currentOrganization : null;

  const handleOpenFullPage = () => {
    onClose();
    navigate(`/admin/marketing/organisations/${orgId}`);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          maxWidth: "100vw",
          bgcolor: theme.palette.background.default,
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }}
      ModalProps={{ keepMounted: false }}
    >
      {/* ── Drawer header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
          Fiche Organisation
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {org && (
            <Button
              size="small"
              startIcon={<Rocket size={14} />}
              onClick={() => setOnboardOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 600,
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                mr: 0.5,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
              }}
            >
              Onboarder
            </Button>
          )}
          <IconButton size="small" onClick={handleOpenFullPage} sx={{ color: theme.palette.text.secondary }}>
            <ExternalLink size={16} />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
            <X size={16} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Content ── */}
      {loading || !org ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <FicheHeader org={org} orgId={orgId} />
          <Divider />
          <FicheInfo org={org} orgId={orgId} />
          <Divider />
          <FicheContacts org={org} orgId={orgId} allContacts={allContacts} />
          <Divider />
          <FicheNotes org={org} orgId={orgId} />
          <Divider />
          <FicheQuotes orgId={orgId} org={org} />
          <Divider />
          <FicheEmail org={org} orgId={orgId} />
          <Divider />
          <FicheTasks orgId={orgId} tasks={orgTasks || []} />
          <Divider />
          <FicheActivity orgId={orgId} activity={orgActivity} />
        </Box>
      )}

      <OnboardOrgDialog
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        org={org}
      />
    </Drawer>
  );
};

export default OrganizationFiche;
